/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import { OperationalLogger } from "./OperationalLogger";
import { DiagnosticSeverity, type LogDetails } from "../types";
import { AxiosErrorAdapter } from "../errorMapper/adapters/AxiosErrorAdapter";
import { ErrorCodeTypes } from "../enums/ErrorCodeTypes";
import { ErrorCodeUtil } from "../utility/ErrorCodeUtil";

/** Caller-supplied soft client certificate telemetry fields. */
export type SoftClientCertLogFields = Record<string, string | undefined>;

const KEY_EVENT: string = "event";
const KEY_ERROR_CODE: string = "error_code";
const KEY_SUB_ERROR_CODE: string = "sub_error_code";
const KEY_ERROR_TYPE: string = "error_type";
const KEY_REASON: string = "reason";
const KEY_VALIDITY_SOURCE: string = "validity_source";
const KEY_EXPIRES_IN_S: string = "expires_in_s";
const KEY_MISSING_FIELDS: string = "missing_fields";

const MESSAGE_FETCH_FAILED: string = "Soft client certificate fetch failed";
const MESSAGE_INVALID_RESPONSE: string =
  "Soft client certificate response is missing its certificate or private key";
const MESSAGE_STORE_FAILED: string =
  "Soft client certificate could not be persisted";

const SOFT_CLIENT_CERT_DIAGNOSTIC_EVENT: string = "soft_client_cert";

/** `soft_client_cert` diagnostic discriminators. */
export const SoftClientCertEvent = {
  CertificateCached: "certificate_cached",
  FetchSucceeded: "fetch_succeeded",
  FetchFailed: "fetch_failed",
  InvalidResponse: "invalid_response",
  StoreFailed: "store_failed"
} as const;

export type SoftClientCertEventName =
  (typeof SoftClientCertEvent)[keyof typeof SoftClientCertEvent];

/** Why a certificate was fetched rather than served from storage. */
export const SoftClientCertFetchReason = {
  /** No stored certificate, an expired one, or one for another bodyId. */
  NotCached: "not_cached",
  /** The certificate record was valid but its private key was gone. */
  KeyMissing: "key_missing"
} as const;

export type SoftClientCertFetchReasonName =
  (typeof SoftClientCertFetchReason)[keyof typeof SoftClientCertFetchReason];

/** Where the stored validity window came from. */
export const SoftClientCertValiditySource = {
  /** The response's `endTime`. */
  Server: "server",
  /** The response carried no usable `endTime`; the default TTL applied. */
  DefaultTtl: "default_ttl"
} as const;

export type SoftClientCertValiditySourceName =
  (typeof SoftClientCertValiditySource)[keyof typeof SoftClientCertValiditySource];

/**
 * Error codes for the Soft Client Certificate failure scenarios, from the
 * active `softClientCertGet` entries of the shared catalog (the
 * `A500`-`A520` `GET /v1/softClientCert` codes are deprecated, and web reuses
 * that range for ip-autologin).
 *
 * - `C687` — the request timed out (`SOFT_CLIENT_CERT_GET_TIMEOUT`).
 * - `C676` — the service refused the device (`NOT_AUTHORIZED` /
 *   `SOFT_CLIENT_CERT_MUST_USE_CERT`).
 * - `C624` — the service answered without a usable certificate
 *   (`DEVICE_SIGN_IN_DEVICE_CERT_FAILURE`).
 * - `C688` — any other failure (`SOFT_CLIENT_CERT_GET_UNEXPECTED_ERROR`),
 *   including a certificate that could not be persisted.
 *
 * A failed request also carries the {@link ErrorCodeUtil} sub-error code of
 * the layer that failed (`C688-N603`, `C676-G401`, …).
 */
export const SoftClientCertErrorCode = {
  TIMEOUT: "C687",
  NOT_AUTHORIZED: "C676",
  CERTIFICATE_FAILURE: "C624",
  UNEXPECTED: "C688"
} as const;

/** Failure layers whose sub-error code means the request timed out. */
const TIMEOUT_ERROR_TYPES: ReadonlyArray<ErrorCodeTypes> = [
  ErrorCodeTypes.TIMEOUT,
  ErrorCodeTypes.SERVER_TIMEOUT
];

/** Failure layers whose sub-error code means the device was refused. */
const NOT_AUTHORIZED_ERROR_TYPES: ReadonlyArray<ErrorCodeTypes> = [
  ErrorCodeTypes.NOT_AUTHORIZED,
  ErrorCodeTypes.FORBIDDEN,
  ErrorCodeTypes.CREDENTIAL_NOT_AUTHORIZED
];

/**
 * Structured telemetry facade for the Soft Client Certificate flow: the
 * certificate the WebSocket FSM's GET_CLIENT_CERT step needs, served from
 * storage or fetched from `POST /v1/softClientCert` and persisted.
 *
 * The request itself is reported by `PostSigninLogger` (`apiRequest`,
 * `api_name=softClientCert`); this logger reports what the flow did with the
 * result. All details are pre-redacted: the certificate, private key and raw
 * error messages are never logged.
 */
export class SoftClientCertLogger {
  private static readonly adapter: AxiosErrorAdapter = new AxiosErrorAdapter();

  /**
   * `error_code` / `sub_error_code` for a failed certificate request. The
   * code is chosen from the failing layer; the sub-error code is omitted when
   * no layer recognised the failure.
   */
  public static getFailureFields(error: unknown): SoftClientCertLogFields {
    const subErrorCode: string = ErrorCodeUtil.extractSubErrorCode(
      error,
      SoftClientCertLogger.adapter
    );
    return {
      [KEY_ERROR_CODE]: SoftClientCertLogger.getRequestErrorCode(subErrorCode),
      [KEY_SUB_ERROR_CODE]: subErrorCode.length > 0 ? subErrorCode : undefined
    };
  }

  /**
   * A valid certificate for this bodyId was already stored.
   *
   * @param endTimeMs - Validity end, when known
   */
  public static logCertificateCached(endTimeMs?: number): void {
    SoftClientCertLogger.log(
      SoftClientCertEvent.CertificateCached,
      "Soft client certificate served from storage",
      DiagnosticSeverity.Debug,
      {
        [KEY_EXPIRES_IN_S]:
          endTimeMs !== undefined
            ? SoftClientCertLogger.secondsUntil(endTimeMs)
            : undefined
      }
    );
  }

  /** A certificate was fetched and persisted. */
  public static logFetchSucceeded(
    reason: SoftClientCertFetchReasonName,
    validitySource: SoftClientCertValiditySourceName,
    endTimeMs: number
  ): void {
    SoftClientCertLogger.log(
      SoftClientCertEvent.FetchSucceeded,
      "Soft client certificate fetched",
      DiagnosticSeverity.Info,
      {
        [KEY_REASON]: reason,
        [KEY_VALIDITY_SOURCE]: validitySource,
        [KEY_EXPIRES_IN_S]: SoftClientCertLogger.secondsUntil(endTimeMs)
      }
    );
  }

  /**
   * The certificate request failed (`C687` / `C676` / `C688`). The WebSocket
   * FSM restarts the connect flow, which retries the fetch.
   */
  public static logFetchFailed(
    reason: SoftClientCertFetchReasonName,
    error: unknown
  ): void {
    const failureFields: SoftClientCertLogFields =
      SoftClientCertLogger.getFailureFields(error);
    SoftClientCertLogger.log(
      SoftClientCertEvent.FetchFailed,
      MESSAGE_FETCH_FAILED,
      DiagnosticSeverity.Warning,
      {
        ...failureFields,
        [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error),
        [KEY_REASON]: reason
      }
    );
    OperationalLogger.logError({
      errorCode: SoftClientCertLogger.toDisplayCode(failureFields),
      errorMessage: MESSAGE_FETCH_FAILED
    });
  }

  /**
   * The response lacked the certificate or private key (`C624`), so nothing
   * was stored and the WebSocket cannot authenticate.
   *
   * @param missingFields - Names of the absent fields, never their values
   */
  public static logInvalidResponse(missingFields: ReadonlyArray<string>): void {
    SoftClientCertLogger.log(
      SoftClientCertEvent.InvalidResponse,
      MESSAGE_INVALID_RESPONSE,
      DiagnosticSeverity.Warning,
      {
        [KEY_MISSING_FIELDS]: missingFields.join(","),
        [KEY_ERROR_CODE]: SoftClientCertErrorCode.CERTIFICATE_FAILURE
      }
    );
    OperationalLogger.logError({
      errorCode: SoftClientCertErrorCode.CERTIFICATE_FAILURE,
      errorMessage: MESSAGE_INVALID_RESPONSE
    });
  }

  /** A fetched certificate could not be written to storage (`C688`). */
  public static logStoreFailed(error: unknown): void {
    SoftClientCertLogger.log(
      SoftClientCertEvent.StoreFailed,
      MESSAGE_STORE_FAILED,
      DiagnosticSeverity.Warning,
      {
        [KEY_ERROR_CODE]: SoftClientCertErrorCode.UNEXPECTED,
        [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error)
      }
    );
    OperationalLogger.logError({
      errorCode: SoftClientCertErrorCode.UNEXPECTED,
      errorMessage: MESSAGE_STORE_FAILED
    });
  }

  /** The catalog code for a failed request, chosen from its sub-error code. */
  private static getRequestErrorCode(subErrorCode: string): string {
    if (subErrorCode.length === 0) {
      return SoftClientCertErrorCode.UNEXPECTED;
    }
    const matches = (types: ReadonlyArray<ErrorCodeTypes>): boolean =>
      types.some(
        (type: ErrorCodeTypes): boolean =>
          ErrorCodeUtil.getSuffixFromErrorType(type) === subErrorCode
      );
    if (matches(TIMEOUT_ERROR_TYPES)) {
      return SoftClientCertErrorCode.TIMEOUT;
    }
    if (matches(NOT_AUTHORIZED_ERROR_TYPES)) {
      return SoftClientCertErrorCode.NOT_AUTHORIZED;
    }
    return SoftClientCertErrorCode.UNEXPECTED;
  }

  /** Seconds from now until an epoch-milliseconds time; negative if past. */
  private static secondsUntil(timeMs: number): string {
    return String(Math.round((timeMs - Date.now()) / 1000));
  }

  /** `C688` or `C688-N603` — the code as a user or support would see it. */
  private static toDisplayCode(fields: SoftClientCertLogFields): string {
    const errorCode: string = fields[KEY_ERROR_CODE] || "";
    const subErrorCode: string | undefined = fields[KEY_SUB_ERROR_CODE];
    return subErrorCode !== undefined
      ? errorCode + "-" + subErrorCode
      : errorCode;
  }

  private static log(
    event: SoftClientCertEventName,
    message: string,
    severity: DiagnosticSeverity,
    extra?: SoftClientCertLogFields
  ): void {
    const details: LogDetails = { [KEY_EVENT]: event };
    if (extra !== undefined) {
      Object.keys(extra).forEach((key: string): void => {
        const value: string | undefined = extra[key];
        if (value !== undefined) {
          details[key] = value;
        }
      });
    }
    DiagnosticLogger.log(
      SOFT_CLIENT_CERT_DIAGNOSTIC_EVENT,
      message,
      severity,
      details
    );
  }
}
